import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Switch, IconButton, AppBar, Toolbar, Button,
    Container, CircularProgress, Alert, Tooltip, TextField, InputAdornment,
    Card, CardContent, Avatar, Stack, useMediaQuery, useTheme, alpha,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControlLabel
} from '@mui/material';
import {
    Logout, Search, AdminPanelSettings, School, Refresh,
    CheckCircle, Cancel, Visibility, ReceiptLong, NotificationImportant, Assignment,
    Group, Person, Lock, LockOpen, Email, TableChart, CloudUpload, Sms, Timer
} from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import SchoolTransactionsModal from '../components/SchoolTransactionsModal';
import ServiceAgreementPDF from '../components/ServiceAgreementPDF';
import ManualAgreementModal from '../components/ManualAgreementModal';
import ManualInvoiceModal from '../components/ManualInvoiceModal';
import ManualReceiptModal from '../components/ManualReceiptModal';
import CreditSmsModal from '../components/CreditSmsModal';
import SendAdminSmsModal from '../components/SendAdminSmsModal';
import SchoolSmsLogsModal from '../components/SchoolSmsLogsModal';
import UploadSchoolSbaModal from '../components/UploadSchoolSbaModal';
import UploadGlobalSbaModal from '../components/UploadGlobalSbaModal';
import ManageClockInExceptionModal from '../components/ManageClockInExceptionModal';
import { useNavigate } from 'react-router-dom';
import api from '../utils/Api';


const TEACHER_DASHBOARD_CARD_FEATURES = [
    { key: 'teacher_attendance_card', label: 'Teacher Attendance' },
    { key: 'student_attendance_card', label: 'Student Attendance' },
    { key: 'teacher_feeding_fee_card', label: 'Feeding Fee' },
    { key: 'teacher_class_fee_card', label: 'Classes Fee' },
    { key: 'teacher_events_card', label: 'Events' },
    { key: 'teacher_announcements_card', label: 'Announcement' },
    { key: 'teacher_assignments_card', label: 'Assignment' },
    { key: 'teacher_online_quiz_card', label: 'Online Quiz' },
    { key: 'teacher_transport_card', label: 'School Bus' },
    { key: 'teacher_timetable_card', label: 'Timetable' },
    { key: 'teacher_weekly_exercise_card', label: 'Weekly Exercise' },
    { key: 'teacher_exams_report_card', label: 'Exams & Report' },
    { key: 'teacher_sba_direct_entry_card', label: 'SBA Direct Entry' },
    { key: 'teacher_payslips_card', label: 'My Payslips' },
];


const SuperAdminDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [stats, setStats] = useState({ total: 0, active: 0, restricted: 0 });
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [manualModalOpen, setManualModalOpen] = useState(false);
    const [manualInvoiceModalOpen, setManualInvoiceModalOpen] = useState(false);
    const [manualReceiptModalOpen, setManualReceiptModalOpen] = useState(false);
    const [globalSbaModalOpen, setGlobalSbaModalOpen] = useState(false);
    const [selectedSchoolDetails, setSelectedSchoolDetails] = useState(null);
    const [creditSmsSchool, setCreditSmsSchool] = useState(null);
    const [sbaUploadSchool, setSbaUploadSchool] = useState(null);
    const [smsBalance, setSmsBalance] = useState(null);
    const [sendSmsSchool, setSendSmsSchool] = useState(null);
    const [smsLogsSchool, setSmsLogsSchool] = useState(null);
    const [exceptionSchool, setExceptionSchool] = useState(null);

    // 🔄 Fetch Schools
    const fetchSchools = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch schools and overall SMS balance in parallel
            const [schoolsRes, smsRes] = await Promise.allSettled([
                api.get('/super-admin/schools'),
                api.get('/sms/balance')
            ]);

            if (schoolsRes.status === 'fulfilled') {
                const schoolData = schoolsRes.value.data?.schools || schoolsRes.value.data?.data || [];
                const dataToUse = Array.isArray(schoolData) ? schoolData : [];
                setSchools(dataToUse);

                // Calculate stats
                const total = dataToUse.length;
                const active = dataToUse.filter(s => s.status === 'active').length;
                const restricted = dataToUse.filter(s => s.status === 'restricted').length;
                setStats({ total, active, restricted });
            } else {
                throw schoolsRes.reason;
            }

            if (smsRes.status === 'fulfilled' && smsRes.value.data?.success) {
                const bal = smsRes.value.data.balance?.balance || smsRes.value.data.balance?.data?.balance || '0.00';
                setSmsBalance(bal);
            }

        } catch (err) {
            console.error("Error fetching super admin dashboard data:", err);
            setError('Failed to load schools. Please ensure you are logged in as Super Admin.');
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate('/super-admin/login');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);


    useEffect(() => {
        fetchSchools();
    }, [fetchSchools]);

    // 🔒 Handle Status Toggle
    const handleStatusToggle = async (schoolId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'restricted' : 'active';
        const action = newStatus === 'active' ? 'activate' : 'restrict';

        if (!window.confirm(`Are you sure you want to ${action} access for this school?`)) return;

        // Optimistic Update
        setSchools(prev => prev.map(s =>
            s._id === schoolId ? { ...s, status: newStatus } : s
        ));

        try {
            await api.put(`/super-admin/schools/${schoolId}/status`, { status: newStatus });
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Failed to update status. Reverting...");
            fetchSchools(); // Revert on error
        }
    };

    // 🔒 Handle Feature Toggle
    const handleFeatureToggle = async (schoolId, featureKey) => {
        const school = schools.find(s => s._id === schoolId);
        if (!school) return;

        const currentLocked = school.lockedFeatures || [];
        const isCurrentlyLocked = currentLocked.includes(featureKey);
        
        let newLockedFeatures;
        if (isCurrentlyLocked) {
            newLockedFeatures = currentLocked.filter(f => f !== featureKey); // Unlock it
        } else {
            newLockedFeatures = [...currentLocked, featureKey]; // Lock it
        }

        // Optimistic Update
        setSchools(prev => prev.map(s =>
            s._id === schoolId ? { ...s, lockedFeatures: newLockedFeatures } : s
        ));
        if (selectedSchoolDetails && selectedSchoolDetails._id === schoolId) {
            setSelectedSchoolDetails(prev => ({ ...prev, lockedFeatures: newLockedFeatures }));
        }

        try {
            await api.put(`/super-admin/schools/${schoolId}/features`, { lockedFeatures: newLockedFeatures });
        } catch (err) {
            console.error("Error updating features:", err);
            alert("Failed to update feature access. Reverting...");
            fetchSchools(); // Revert on error
        }
    };

    // 🔔 Alert Owing School
    const handleAlertOwingSchool = async (schoolId, schoolName) => {
        if (!window.confirm(`Are you sure you want to send an overdue subscription alert to ${schoolName}?`)) return;
        try {
            const res = await api.post(`/super-admin/schools/${schoolId}/alert-owing`);
            alert(res.data.message || 'Alert sent successfully');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to send alert');
        }
    };

    // 🚪 Logout
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        api.post('/auth/logout').catch(err => console.error(err));
        navigate('/super-admin/login');
    };

    // 🔍 Filter Schools
    // Get initials for avatar
    const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

    const getSchoolEmail = (school = {}) =>
        school.schoolEmail ||
        school.schoolInfo?.email ||
        school.email ||
        school.primaryAdmin?.email ||
        '';

    const filteredSchools = schools.filter(s => {
        const query = search.toLowerCase();
        return (
            s.name?.toLowerCase().includes(query) ||
            getSchoolEmail(s).toLowerCase().includes(query) ||
            s.schoolType?.toLowerCase().includes(query)
        );
    });

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* 🚀 Super Admin AppBar */}
            <AppBar
                position="static"
                sx={{
                    background: 'linear-gradient(90deg, #1a237e 0%, #283593 100%)',
                    boxShadow: 3,
                    color: 'white'
                }}
            >
                <Toolbar>
                    <Avatar
                        sx={{
                            bgcolor: 'white',
                            color: theme.palette.primary.main,
                            mr: 2,
                            width: isMobile ? 40 : 48,
                            height: isMobile ? 40 : 48
                        }}
                    >
                        <AdminPanelSettings />
                    </Avatar>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        component="div"
                        sx={{ flexGrow: 1, fontWeight: 'bold', color: 'white' }}
                    >
                        Super Admin Control Center
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            color="inherit"
                            onClick={() => navigate('/super-admin/release-notes')}
                            startIcon={<NotificationImportant />}
                            size={isMobile ? "small" : "medium"}
                            sx={{ textTransform: 'none', fontWeight: 800 }}
                        >
                            {isMobile ? '' : "What's New"}
                        </Button>
                        <Button
                            color="inherit"
                            onClick={handleLogout}
                            startIcon={<Logout />}
                            size={isMobile ? "small" : "medium"}
                        >
                            {isMobile ? '' : 'Logout'}
                        </Button>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Container
                maxWidth="xl"
                sx={{
                    py: isMobile ? 2 : 4,
                    px: isMobile ? 1 : 2
                }}
            >
                {/* 📊 Stats Cards - Using Grid v2 with Card styling from StudentAttendancePage */}
                <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            boxShadow: isMobile ? 1 : 3,
                            border: `1px solid ${theme.palette.primary.main}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: isMobile ? 'none' : 'translateY(-4px)',
                                boxShadow: isMobile ? 2 : 6
                            }
                        }}>
                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.primary.main,
                                            width: isMobile ? 36 : 48,
                                            height: isMobile ? 36 : 48
                                        }}
                                    >
                                        <School fontSize={isMobile ? "small" : "medium"} />
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant={isMobile ? "caption" : "h6"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                                        >
                                            Total Schools
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "h5" : "h4"}
                                            fontWeight="bold"
                                            sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                                        >
                                            {stats.total}
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "caption" : "body2"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                        >
                                            Registered institutions
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            boxShadow: isMobile ? 1 : 3,
                            border: `1px solid ${theme.palette.success.main}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: isMobile ? 'none' : 'translateY(-4px)',
                                boxShadow: isMobile ? 2 : 6
                            }
                        }}>
                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.success.main,
                                            width: isMobile ? 36 : 48,
                                            height: isMobile ? 36 : 48
                                        }}
                                    >
                                        <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant={isMobile ? "caption" : "h6"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                                        >
                                            Active
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "h5" : "h4"}
                                            fontWeight="bold"
                                            color="success.main"
                                            sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                                        >
                                            {stats.active}
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "caption" : "body2"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                        >
                                            Schools with full access
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            boxShadow: isMobile ? 1 : 3,
                            border: `1px solid ${theme.palette.error.main}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: isMobile ? 'none' : 'translateY(-4px)',
                                boxShadow: isMobile ? 2 : 6
                            }
                        }}>
                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.error.main,
                                            width: isMobile ? 36 : 48,
                                            height: isMobile ? 36 : 48
                                        }}
                                    >
                                        <Cancel fontSize={isMobile ? "small" : "medium"} />
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant={isMobile ? "caption" : "h6"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                                        >
                                            Restricted
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "h5" : "h4"}
                                            fontWeight="bold"
                                            color="error.main"
                                            sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                                        >
                                            {stats.restricted}
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "caption" : "body2"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                        >
                                            Access limited
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            boxShadow: isMobile ? 1 : 3,
                            border: `1px solid ${theme.palette.info.main}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: isMobile ? 'none' : 'translateY(-4px)',
                                boxShadow: isMobile ? 2 : 6
                            }
                        }}>
                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.info.main,
                                            width: isMobile ? 36 : 48,
                                            height: isMobile ? 36 : 48
                                        }}
                                    >
                                        <Sms fontSize={isMobile ? "small" : "medium"} />
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant={isMobile ? "caption" : "h6"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                                        >
                                            SMS Balance
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "h5" : "h4"}
                                            fontWeight="bold"
                                            color="info.main"
                                            sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                                        >
                                            {smsBalance !== null ? smsBalance : '—'}
                                        </Typography>
                                        <Typography
                                            variant={isMobile ? "caption" : "body2"}
                                            color="text.secondary"
                                            sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                        >
                                            Master Arkesel account
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>


                {/* 🛠️ Management Table */}
                <Card sx={{
                    boxShadow: isMobile ? 1 : 3,
                    border: `1px solid ${theme.palette.divider}`
                }}>
                    <CardContent sx={{ p: 0 }}>
                        {/* Header */}
                        <Box
                            sx={{
                                p: isMobile ? 2 : 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2
                            }}
                        >
                            <Typography
                                variant={isMobile ? "h6" : "h6"}
                                fontWeight="600"
                                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                            >
                                Registered Schools ({filteredSchools.length})
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <TextField
                                    size="small"
                                    placeholder="Search schools..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: isMobile ? 200 : 300 }}
                                />
                                <Tooltip title="Manual Service Agreement">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Assignment />}
                                        onClick={() => setManualModalOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            display: isMobile ? 'none' : 'flex'
                                        }}
                                    >
                                        Manual Agreement
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Manual Invoice generating">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        startIcon={<ReceiptLong />}
                                        onClick={() => setManualInvoiceModalOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            display: isMobile ? 'none' : 'flex'
                                        }}
                                    >
                                        Manual Invoice
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Manual Receipt generating">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="success"
                                        startIcon={<ReceiptLong />}
                                        onClick={() => setManualReceiptModalOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            display: isMobile ? 'none' : 'flex'
                                        }}
                                    >
                                        Manual Receipt
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Upload Global SBA Master">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                        startIcon={<CloudUpload />}
                                        onClick={() => setGlobalSbaModalOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            display: isMobile ? 'none' : 'flex'
                                        }}
                                    >
                                        Global SBA
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Refresh Data">
                                    <IconButton onClick={fetchSchools} size="small">
                                        <Refresh />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>

                        {loading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                        ) : (
                            <TableContainer sx={{ maxHeight: isTablet ? '60vh' : 'none' }}>
                                <Table stickyHeader>
                                    <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>School</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Type</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>School Email</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Last Visit</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Subscription</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredSchools.length > 0 ? (
                                            filteredSchools.map((school) => {
                                                const isOwing = school.subscription?.isOwing;
                                                const balance = school.subscription?.owingBalance || 0;
                                                const nextDueDate = school.subscription?.nextDueDate;
                                                const schoolEmail = getSchoolEmail(school);
                                                return (
                                                    <TableRow
                                                        key={school._id}
                                                        hover
                                                        sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                                                    >
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Stack 
                                                                direction="row" 
                                                                alignItems="center" 
                                                                spacing={2}
                                                                onClick={() => setSelectedSchoolDetails(school)}
                                                                sx={{ 
                                                                    cursor: 'pointer', 
                                                                    '&:hover': { opacity: 0.8 },
                                                                    borderRadius: 1,
                                                                    p: 0.5
                                                                }}
                                                            >
                                                                <Avatar
                                                                    sx={{
                                                                        bgcolor: theme.palette.primary.main,
                                                                        width: 40,
                                                                        height: 40
                                                                    }}
                                                                >
                                                                    {getInitials(school.name)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle1" fontWeight="500" color="primary.main" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                                                                        {school.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Joined: {new Date(school.createdAt).toLocaleDateString()}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={school.schoolType || 'Private'}
                                                                size="small"
                                                                color={school.schoolType === 'Government' ? 'info' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {schoolEmail ? (
                                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                    <Email fontSize="small" color="action" />
                                                                    <Typography
                                                                        component="a"
                                                                        href={`mailto:${schoolEmail}`}
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'primary.main',
                                                                            textDecoration: 'none',
                                                                            '&:hover': { textDecoration: 'underline' }
                                                                        }}
                                                                    >
                                                                        {schoolEmail}
                                                                    </Typography>
                                                                </Stack>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {school.lastAdminLogin ? (
                                                                <Stack>
                                                                    <Typography variant="body2">{new Date(school.lastAdminLogin).toLocaleDateString()}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {new Date(school.lastAdminLogin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    </Typography>
                                                                </Stack>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">Never</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {isOwing ? (
                                                                <Stack spacing={0.5}>
                                                                    <Typography variant="body2" color="error" fontWeight="bold">
                                                                        Owing: ₵{balance.toFixed(2)}
                                                                    </Typography>
                                                                    {nextDueDate && (
                                                                        <Typography variant="caption" color="error">
                                                                            Due: {new Date(nextDueDate).toLocaleDateString()}
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            ) : (
                                                                <Typography variant="body2" color="success.main" fontWeight="bold">
                                                                    Paid
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={school.status === 'restricted' ? 'Restricted' : 'Active'}
                                                                color={school.status === 'restricted' ? 'error' : 'success'}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                                                {isOwing && (
                                                                    <Tooltip title="Send Overdue Alert">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleAlertOwingSchool(school._id, school.name)}
                                                                        >
                                                                            <NotificationImportant fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}

                                                                <Tooltip title="Download Service Agreement Contract">
                                                                    <Box component="span">
                                                                        <PDFDownloadLink
                                                                            document={<ServiceAgreementPDF school={school} />}
                                                                            fileName={`Agreement_${school.name.replace(/\s+/g, '_')}.pdf`}
                                                                            style={{ textDecoration: 'none' }}
                                                                        >
                                                                            {({ loading }) => (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="secondary"
                                                                                    disabled={loading}
                                                                                >
                                                                                    <Assignment fontSize="small" />
                                                                                </IconButton>
                                                                            )}
                                                                        </PDFDownloadLink>
                                                                    </Box>
                                                                </Tooltip>
                                                                <Tooltip title="View Transactions (Invoices/Receipts)">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => setSelectedSchool(school)}
                                                                    >
                                                                        <ReceiptLong fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Credit SMS Balance">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="info"
                                                                        onClick={() => setCreditSmsSchool(school)}
                                                                    >
                                                                        <Email fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Send SMS to Admin">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="success"
                                                                        onClick={() => setSendSmsSchool(school)}
                                                                    >
                                                                        <Sms fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="View SMS Logs">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="info"
                                                                        onClick={() => setSmsLogsSchool(school)}
                                                                    >
                                                                        <TableChart fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Manage Clock-In Exceptions">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => setExceptionSchool(school)}
                                                                    >
                                                                        <Timer fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Upload Custom SBA">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="warning"
                                                                        onClick={() => setSbaUploadSchool(school)}
                                                                    >
                                                                        <TableChart fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={school.status === 'active' ? "Restrict Access" : "Restore Access"}>
                                                                    <Switch
                                                                        checked={school.status === 'active'}
                                                                        onChange={() => handleStatusToggle(school._id, school.status)}
                                                                        color="success"
                                                                    />
                                                                </Tooltip>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                    <Stack spacing={1} alignItems="center">
                                                        <Visibility sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                                                        <Typography color="text.secondary">
                                                            No schools found matching your search.
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Optional Legend (like attendance page) */}
                <Card sx={{ mt: 3, boxShadow: isMobile ? 1 : 2 }}>
                    <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                        <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={isMobile ? 2 : 4}
                            alignItems={isMobile ? "flex-start" : "center"}
                        >
                            <Typography
                                variant={isMobile ? "body2" : "subtitle1"}
                                fontWeight="600"
                                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                            >
                                Legend:
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <CheckCircle color="success" fontSize="small" />
                                <Typography variant="body2">Active</Typography>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Cancel color="error" fontSize="small" />
                                <Typography variant="body2">Restricted</Typography>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Container>

            {selectedSchool && (
                <SchoolTransactionsModal
                    open={!!selectedSchool}
                    onClose={() => setSelectedSchool(null)}
                    school={selectedSchool}
                />
            )}

            {manualModalOpen && (
                <ManualAgreementModal
                    open={manualModalOpen}
                    onClose={() => setManualModalOpen(false)}
                />
            )}

            {manualInvoiceModalOpen && (
                <ManualInvoiceModal
                    open={manualInvoiceModalOpen}
                    onClose={() => setManualInvoiceModalOpen(false)}
                />
            )}

            {manualReceiptModalOpen && (
                <ManualReceiptModal
                    open={manualReceiptModalOpen}
                    onClose={() => setManualReceiptModalOpen(false)}
                />
            )}

            {creditSmsSchool && (
                <CreditSmsModal
                    open={!!creditSmsSchool}
                    onClose={() => setCreditSmsSchool(null)}
                    school={creditSmsSchool}
                    onCreditSuccess={() => {
                        // Optionally refresh schools or show a global alert
                        fetchSchools();
                    }}
                />
            )}

            {sbaUploadSchool && (
                <UploadSchoolSbaModal
                    open={!!sbaUploadSchool}
                    onClose={() => setSbaUploadSchool(null)}
                    school={sbaUploadSchool}
                />
            )}

            {globalSbaModalOpen && (
                <UploadGlobalSbaModal
                    open={globalSbaModalOpen}
                    onClose={() => setGlobalSbaModalOpen(false)}
                />
            )}

            {sendSmsSchool && (
                <SendAdminSmsModal
                    open={!!sendSmsSchool}
                    onClose={() => setSendSmsSchool(null)}
                    school={sendSmsSchool}
                />
            )}

            {smsLogsSchool && (
                <SchoolSmsLogsModal
                    open={!!smsLogsSchool}
                    onClose={() => setSmsLogsSchool(null)}
                    school={smsLogsSchool}
                />
            )}

            {exceptionSchool && (
                <ManageClockInExceptionModal
                    open={!!exceptionSchool}
                    onClose={() => setExceptionSchool(null)}
                    school={exceptionSchool}
                />
            )}

            {/* 🏫 School Details Dialog */}
            <Dialog 
                open={!!selectedSchoolDetails} 
                onClose={() => setSelectedSchoolDetails(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        School Information
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedSchoolDetails && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    School Name
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedSchoolDetails.name}
                                </Typography>
                            </Box>
                            
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Contact Email
                                </Typography>
                                <Typography variant="body1">
                                    {getSchoolEmail(selectedSchoolDetails) || 'N/A'}
                                </Typography>
                            </Box>
                            
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    School Type
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSchoolDetails.schoolType || 'Private'}
                                </Typography>
                            </Box>

                            <Divider />

                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
                                Demographics & Statistics
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                                <Group color="primary" fontSize="small" />
                                                <Typography variant="body2" fontWeight="bold" color="primary.main">
                                                    Enrollment
                                                </Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight="bold">
                                                {selectedSchoolDetails.stats?.students || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Total Students
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6}>
                                    <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05), border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                                <Person color="secondary" fontSize="small" />
                                                <Typography variant="body2" fontWeight="bold" color="secondary.main">
                                                    Staff
                                                </Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight="bold">
                                                {(selectedSchoolDetails.stats?.teachers || 0) + (selectedSchoolDetails.stats?.admins || 0)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {selectedSchoolDetails.stats?.teachers || 0} T / {selectedSchoolDetails.stats?.admins || 0} A
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" fontWeight="bold">
                                Feature Access Management
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Toggle switches to restrict or allow access to specific modules for this school.
                            </Typography>

                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Core Module Access
                                </Typography>

                                {/* Exams Toggle */}
                                <Card variant="outlined" sx={{ bgcolor: selectedSchoolDetails.lockedFeatures?.includes('exams') ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05) }}>
                                    <CardContent sx={{ p: '12px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {selectedSchoolDetails.lockedFeatures?.includes('exams') ? <Lock color="error" fontSize="small" /> : <LockOpen color="success" fontSize="small" />}
                                            <Typography variant="body1" fontWeight="500">
                                                Exams Module
                                            </Typography>
                                        </Stack>
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    checked={!selectedSchoolDetails.lockedFeatures?.includes('exams')} 
                                                    onChange={() => handleFeatureToggle(selectedSchoolDetails._id, 'exams')} 
                                                    color="success"
                                                />
                                            }
                                            label={selectedSchoolDetails.lockedFeatures?.includes('exams') ? "Locked" : "Unlocked"}
                                            labelPlacement="start"
                                            sx={{ m: 0 }}
                                        />
                                    </CardContent>
                                </Card>

                                {/* Reports Toggle */}
                                <Card variant="outlined" sx={{ bgcolor: selectedSchoolDetails.lockedFeatures?.includes('reports') ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05) }}>
                                    <CardContent sx={{ p: '12px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {selectedSchoolDetails.lockedFeatures?.includes('reports') ? <Lock color="error" fontSize="small" /> : <LockOpen color="success" fontSize="small" />}
                                            <Typography variant="body1" fontWeight="500">
                                                Reports Module
                                            </Typography>
                                        </Stack>
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    checked={!selectedSchoolDetails.lockedFeatures?.includes('reports')} 
                                                    onChange={() => handleFeatureToggle(selectedSchoolDetails._id, 'reports')} 
                                                    color="success"
                                                />
                                            }
                                            label={selectedSchoolDetails.lockedFeatures?.includes('reports') ? "Locked" : "Unlocked"}
                                            labelPlacement="start"
                                            sx={{ m: 0 }}
                                        />
                                    </CardContent>
                                </Card>

                                <Divider sx={{ my: 1.5 }} />

                                <Box>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Teacher Mobile Dashboard Cards
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                                        Turn cards on or off for teachers in this school. Hidden cards will not appear on the teacher mobile dashboard.
                                    </Typography>
                                </Box>

                                <Grid container spacing={1}>
                                    {TEACHER_DASHBOARD_CARD_FEATURES.map((feature) => {
                                        const isHidden = selectedSchoolDetails.lockedFeatures?.includes(feature.key);

                                        return (
                                            <Grid item xs={12} sm={6} key={feature.key}>
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        bgcolor: isHidden ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05),
                                                        height: '100%',
                                                    }}
                                                >
                                                    <CardContent sx={{ p: '12px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                            {isHidden ? <Lock color="error" fontSize="small" /> : <LockOpen color="success" fontSize="small" />}
                                                            <Typography variant="body2" fontWeight="500" noWrap>
                                                                {feature.label}
                                                            </Typography>
                                                        </Stack>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={!isHidden}
                                                                    onChange={() => handleFeatureToggle(selectedSchoolDetails._id, feature.key)}
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                            }
                                                            label={isHidden ? "Hidden" : "Visible"}
                                                            labelPlacement="start"
                                                            sx={{
                                                                m: 0,
                                                                '& .MuiFormControlLabel-label': {
                                                                    fontSize: '0.75rem',
                                                                    color: isHidden ? 'error.main' : 'success.main',
                                                                    fontWeight: 600,
                                                                },
                                                            }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedSchoolDetails(null)} color="inherit">Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SuperAdminDashboard;
